import { useQuery } from '@tanstack/react-query';
import { FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import Button from '@/components/Button';
import { Box } from '@/components/Dropdown';
import Text from '@/components/Text';
import useCompaniesOptions from '@/hooks/useCompaniesOptions';
import useDepartmentsOptions from '@/hooks/useDepartmentsOptions';
import useFreelancerRolesOptions from '@/hooks/useFreelancerRolesOptions';
import useFreelancerSubunitsOptions from '@/hooks/useFreelancerSubunitsOptions';
import IdFilterDropdown from '@/pages/InvoiceList/components/IdFilterDropdown';
import InvoicesCountriesDropdown from '@/pages/InvoiceList/components/InvoicesCountriesDropdown';
import { api } from '@/utils/api';
import { countryList } from '@/utils/data';
import { genders } from '@/utils/data/payments';
import { Dispatch, RootState } from '@/utils/store';

import FilterBooleanDropdown from './FilterBooleanDropdown';
import FreelancersFilterDropdown from './FreelancersFilterDropdown';

const StyledBox = styled(Box)`
  width: 100%;
  padding: 26px 24px 32px;
`;

const FilterHeading = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
`;

const FilterFooter = styled.div`
  margin-top: 24px;
  text-align: right;
`;

const BodyHeaderLine = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.colors.grayscaleLight};
  flex: 1;
  margin: 0 16px;
`;

const ResetButton = styled.button`
  background: ${({ theme }) => theme.colors.errorLight};
  height: 23px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  border-radius: 4px;
  border: 1px solid transparent;
  transition: all ease-out 0.2s;
  &:hover,
  &:active {
    border-color: ${({ theme }) => theme.colors.error};
  }
  &:disabled {
    background: ${({ theme }) => theme.colors.grayscaleLight};
    cursor: not-allowed;
    &:hover,
    &:active {
      border-color: transparent;
    }
  }
`;

const FilterGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-gap: 16px 20px;
`;

const StyledButton = styled(Button)`
  font-size: 14px;
  min-width: 120px;
  height: 38px;
  padding: 0 20px;
`;

const ApplyButton = styled(StyledButton)`
  margin-left: 16px;
`;

interface Props {
  closeFilter: () => void;
}

const FreelancersFilterBox: FC<Props> = ({ closeFilter }) => {
  const filterOptions = useSelector(
    (rootState: RootState) => rootState.filters.filters.freelancerFilters,
  );

  const [filterBooleanValues, setFilterBooleanValues] = useState<{
    insurance: boolean | string[] | number[] | undefined;
    isBackboneUser?: boolean | string[] | number[] | undefined;
  }>({
    insurance: filterOptions?.insurance,
    isBackboneUser: filterOptions?.isBackboneUser,
  });

  const [selectedLocalDepartments, setSelectedLocalDepartments] = useState<
    number[]
  >((filterOptions?.departments ?? []) as number[]);

  const { data: departmentOptions = [] } = useDepartmentsOptions();
  const { data: companyOptions = [] } = useCompaniesOptions();
  const { data: subunitOptions = [] } = useFreelancerSubunitsOptions();
  const { data: roleOptions = [] } = useFreelancerRolesOptions();

  const [selectedLocalRoles, setSelectedLocalRoles] = useState<number[]>(
    (filterOptions?.roles ?? []) as number[],
  );

  const [selectedLocalCategories, setSelectedLocalCategories] = useState<
    boolean | string[]
  >((filterOptions?.categories ?? []) as boolean | string[]);

  const [selectedLocalCompanies, setSelectedLocalCompanies] = useState<
    number[]
  >((filterOptions?.companies ?? []) as number[]);

  const [selectedLocalSubunits, setSelectedLocalSubunits] = useState<number[]>(
    (filterOptions?.subunits ?? []) as number[],
  );

  const [selectedLocalTeamLeads, setSelectedLocalTeamLeads] = useState<
    number[]
  >((filterOptions?.team_leads ?? []) as number[]);

  const [selectedLocalCountries, setSelectedLocalCountries] = useState<
    boolean | string[]
  >((filterOptions?.countries ?? []) as boolean | string[]);

  const [selectedLocalEmploymentTypes, setSelectedLocalEmploymentTypes] =
    useState<boolean | string[]>(
      (filterOptions?.employment_types ?? []) as boolean | string[],
    );

  const [selectedLocalGender, setSelectedLocalGender] = useState<
    boolean | string[]
  >((filterOptions?.gender ?? []) as boolean | string[]);

  const dispatch = useDispatch<Dispatch>();

  const onFilterApply = () => {
    dispatch.filters.setFilterGroup({
      filterGroup: 'freelancerFilters',
      newOptions: {
        insurance: filterBooleanValues.insurance,
        isBackboneUser: filterBooleanValues.isBackboneUser,
        departments: selectedLocalDepartments,
        roles: selectedLocalRoles,
        categories: selectedLocalCategories,
        companies: selectedLocalCompanies,
        subunits: selectedLocalSubunits,
        team_leads: selectedLocalTeamLeads,
        employment_types: selectedLocalEmploymentTypes,
        countries: selectedLocalCountries,
        gender: selectedLocalGender,
      },
    });

    closeFilter();
  };

  const onFilterReset = () => {
    dispatch.filters.setFilterGroup({
      filterGroup: 'freelancerFilters',
      newOptions: {
        departments: [],
        roles: [],
        categories: [],
        companies: [],
        subunits: [],
        team_leads: [],
        employment_types: [],
        countries: [],
        gender: [],
      },
    });
    closeFilter();
  };

  const { data } = useQuery(
    ['freelancers_filter_data'],
    () =>
      api.get<
        never,
        {
          departments: string[];
          categories: string[];
          companies: string[];
          team_leads: { id: number; name: string }[];
          employment_types: string[];
          countries: string[];
        }
      >('/v1/payments/freelancers-filter-data'),
    {
      staleTime: 60 * 1000,
    },
  );

  const teamLeadOptions = (data?.team_leads ?? [])
    .filter((tl) => tl !== null)
    .map((tl) => ({ name: tl.name, value: tl.id }));

  const countries = countryList.reduce(
    (
      acc: { name: string; value: string }[],
      curr: { name: string; 'alpha-2': string; 'country-code': string },
    ) => {
      return data?.countries.includes(curr['alpha-2'])
        ? [
            ...acc,
            {
              name: curr.name,
              value: curr['alpha-2'],
            },
          ]
        : acc;
    },
    [],
  );

  return (
    <StyledBox>
      <FilterHeading>
        <Text $type="button">Filter</Text>
        <BodyHeaderLine />
        <ResetButton onClick={() => onFilterReset()}>
          <Text $color="error" $type="smallHeadline">
            Reset
          </Text>
        </ResetButton>
      </FilterHeading>
      <FilterGroup>
        <div>
          <FreelancersFilterDropdown
            filter={
              Array.isArray(selectedLocalCategories)
                ? selectedLocalCategories
                : []
            }
            setFilter={setSelectedLocalCategories}
            options={data?.categories.filter((category) => category !== null)}
            title="By Category:"
          />
        </div>
        <div>
          <IdFilterDropdown
            filter={selectedLocalCompanies}
            setFilter={setSelectedLocalCompanies}
            options={companyOptions.map((p) => ({
              name: p.label,
              value: p.value,
            }))}
            title="By Company:"
          />
        </div>
        <div>
          <IdFilterDropdown
            filter={selectedLocalDepartments}
            setFilter={setSelectedLocalDepartments}
            options={departmentOptions.map((dept) => ({
              name: dept.label,
              value: dept.value,
            }))}
            title="By Department:"
          />
        </div>
        <div>
          <IdFilterDropdown
            filter={selectedLocalSubunits}
            setFilter={setSelectedLocalSubunits}
            options={subunitOptions.map((s) => ({
              name: s.label,
              value: s.value,
            }))}
            title="By Subunit:"
          />
        </div>
        <div>
          <IdFilterDropdown
            filter={selectedLocalRoles}
            setFilter={setSelectedLocalRoles}
            options={roleOptions.map((r) => ({
              name: r.label,
              value: r.value,
            }))}
            title="By Role:"
          />
        </div>
        <div>
          <IdFilterDropdown
            filter={selectedLocalTeamLeads}
            setFilter={setSelectedLocalTeamLeads}
            options={teamLeadOptions}
            title="By Team Lead:"
          />
        </div>
        <div>
          <FreelancersFilterDropdown
            filter={
              Array.isArray(selectedLocalEmploymentTypes)
                ? selectedLocalEmploymentTypes
                : []
            }
            setFilter={setSelectedLocalEmploymentTypes}
            options={data?.employment_types.filter(
              (employment_type) => employment_type !== null,
            )}
            title="By Employment Type:"
          />
        </div>
        <div>
          <FreelancersFilterDropdown
            filter={
              Array.isArray(selectedLocalGender) ? selectedLocalGender : []
            }
            setFilter={setSelectedLocalGender}
            options={genders.map((g) => g.value)}
            title="By Gender:"
          />
        </div>
        <InvoicesCountriesDropdown
          filter={
            Array.isArray(selectedLocalCountries) ? selectedLocalCountries : []
          }
          setFilter={setSelectedLocalCountries}
          options={countries}
          title="By Country:"
        />
        <div>
          <FilterBooleanDropdown
            label={'By Insurance:'}
            value={filterBooleanValues.insurance}
            setValue={setFilterBooleanValues}
            name="insurance"
          />
        </div>
        <div>
          <FilterBooleanDropdown
            label={'Is Backbone User:'}
            value={filterBooleanValues.isBackboneUser}
            setValue={setFilterBooleanValues}
            name="isBackboneUser"
          />
        </div>
      </FilterGroup>
      <FilterFooter>
        <StyledButton onClick={closeFilter} color="secondary">
          Cancel
        </StyledButton>
        <ApplyButton color="colorized" onClick={onFilterApply}>
          Apply filter
        </ApplyButton>
      </FilterFooter>
    </StyledBox>
  );
};

export default FreelancersFilterBox;
